'use client';

import clsx from 'clsx';
import React, {
  useCallback, useId, useMemo, useState
} from 'react';
import { Group, Panel, Separator } from 'react-resizable-panels';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { Heading } from '@/components/typography/heading';
import { Text } from '@/components/typography/text';
import { useContracts } from '@/hooks/queries/useDataContracts';
import { useAuthContext } from '@/hooks/useAuthContext';

import ChartPanel from './components/ChartPanel';
import DatabaseExplorer from './components/DatabaseExplorer';
import { ErrorBoundary } from './components/ErrorBoundary';
import ExplorerChat from './components/ExplorerChat';
import FeatureStatsPanel from './components/FeatureStatsPanel';
import ResultsGrid from './components/ResultsGrid';
import SQLEditor from './components/SQLEditor';
import styles from './explorer.module.scss';
import { useAgentChat } from './hooks/useAgentChat';
import { useQueryExecution } from './hooks/useQueryExecution';


export default function DataExplorerPage() {
  const { sessionContext } = useAuthContext();
  const id = useId();

  // Contracts data for tree
  const { data: contractsData, isLoading: contractsLoading } = useContracts();
  const contracts = useMemo( () => ( contractsData?.contracts ?? [] ).filter( c => c.status === 'COMPLETED' ),
    [ contractsData ]
  );

  // SQL editor state
  const [ sqlValue, setSqlValue ] = useState( '' );

  // Panel visibility toggles
  const [ showChart, setShowChart ] = useState( true );
  const [ showStats, setShowStats ] = useState( false );

  // Result tab state
  const [ activeResultTab, setActiveResultTab ] = useState<'results' | 'queries' | 'statistics'>( 'results' );

  // Query execution
  const {
    queryResult,
    isExecuting,
    startExecution,
    updateResult,
    completeExecution,
    setError
  } = useQueryExecution();

  // Agent chat
  const {
    input,
    isWaiting,
    displayMessages,
    queryLogs,
    messagesEndRef,
    onClickChat,
    onInputChange,
    handleStop,
    handleExecuteSQL
  } = useAgentChat( {
    sessionContext,
    sqlValue,
    updateResult,
    completeExecution,
    startExecution,
    setError,
    setSqlValue,
    setActiveResultTab
  } );

  // Tree interaction: double-click a table name to insert it into the SQL editor
  const handleTableDoubleClick = useCallback( ( tableName: string ) => {
    setSqlValue( prev => ( prev ? `${ prev } ${ tableName }` : tableName ) );
  }, [] );

  return (
    <div className={ clsx( 'page', 'no_overflow' ) } data-testid="data-explorer-page" style={ { padding: '0.5rem' } }>
      <ToastContainer />
      <div className={ styles.pageTitle }>
        <Heading as="h1" size="md">Database Explorer <span className={ styles.betaBadge }>BETA</span></Heading>
        <Text size="md">Explore and analyze your data using natural language, with instant SQL generation and insights.</Text>
      </div>
      <div className={ styles.explorerContainer }>
        <Group orientation="horizontal" style={ { overflow: 'hidden' } }>
          {/* Left Panel: Database Explorer */}
          <Panel defaultSize="22%" minSize="15%" maxSize="35%">
            <DatabaseExplorer
              contracts={ contracts }
              isLoading={ contractsLoading }
              onTableDoubleClick={ handleTableDoubleClick }
            />
          </Panel>

          <Separator className={ styles.resizeHandle } style={ { flexBasis: '4px' } } />

          {/* Middle Panel: SQL Editor + Results */}
          <Panel defaultSize="50%" minSize="30%">
            <Group orientation="vertical" style={ { height: '100%' } }>
              <Panel defaultSize="45%" minSize="20%">
                <SQLEditor
                  value={ sqlValue }
                  onChange={ setSqlValue }
                  onExecute={ () => handleExecuteSQL() }
                  isExecuting={ isExecuting }
                  showChart={ showChart }
                  onToggleChart={ () => setShowChart( v => !v ) }
                  showStats={ showStats }
                  onToggleStats={ () => {
                    setShowStats( v => !v );
                    if ( !showStats ) setActiveResultTab( 'statistics' );
                    else if ( activeResultTab === 'statistics' ) setActiveResultTab( 'results' );
                  } }
                />
              </Panel>

              <Separator className={ styles.resizeHandleVertical } style={ { flexBasis: '4px' } } />

              <Panel defaultSize="55%" minSize="20%">
                <div className={ styles.middlePanel }>
                  <div className={ styles.resultsHeader }>
                    <button
                      type="button"
                      data-testid="results-tab"
                      className={ clsx( styles.resultsTab, activeResultTab === 'results' && styles.resultsTab_active ) }
                      onClick={ () => setActiveResultTab( 'results' ) }
                    >
                      Results
                    </button>
                    <button
                      type="button"
                      data-testid="queries-tab"
                      className={ clsx( styles.resultsTab, activeResultTab === 'queries' && styles.resultsTab_active ) }
                      onClick={ () => setActiveResultTab( 'queries' ) }
                    >
                      Queries
                      { queryLogs.length > 0 && <span className={ styles.queryLogBadge }>{ queryLogs.length }</span> }
                    </button>
                    { showStats && (
                      <button
                        type="button"
                        data-testid="statistics-tab"
                        className={ clsx(
                          styles.resultsTab,
                          activeResultTab === 'statistics' && styles.resultsTab_active,
                          ( !queryResult || !queryResult.rows.length ) && styles.resultsTab_disabled
                        ) }
                        onClick={ () => {
                          if ( queryResult && queryResult.rows.length ) {
                            setActiveResultTab( 'statistics' );
                          }
                        } }
                        title={ !queryResult || !queryResult.rows.length ? 'Run a query first' : 'View data statistics' }
                      >
                        Statistics
                      </button>
                    ) }
                  </div>
                  { activeResultTab === 'results' ? (
                    <div className={ styles.resultsWithChart }>
                      <ErrorBoundary>
                        { showChart ? (
                          <Group orientation="vertical" style={ { height: '100%' } }>
                            <Panel defaultSize="60%" minSize="20%">
                              <ResultsGrid result={ queryResult } isExecuting={ isExecuting } sql={ sqlValue } />
                            </Panel>
                            <Separator className={ styles.resizeHandleVertical } style={ { flexBasis: '4px' } } />
                            <Panel defaultSize="40%" minSize="10%">
                              <ChartPanel result={ queryResult } isExecuting={ isExecuting } />
                            </Panel>
                          </Group>
                        ) : (
                          <ResultsGrid result={ queryResult } isExecuting={ isExecuting } sql={ sqlValue } />
                        ) }
                      </ErrorBoundary>
                    </div>
                  ) : activeResultTab === 'statistics' ? (
                    <div className={ styles.resultsWithChart }>
                      <ErrorBoundary>
                        <FeatureStatsPanel result={ queryResult } />
                      </ErrorBoundary>
                    </div>
                  ) : (
                    <div className={ styles.resultsGrid }>
                      { queryLogs.length === 0 ? (
                        <div className={ styles.resultsEmpty }>
                          <span>No queries recorded yet</span>
                        </div>
                      ) : (
                        <div className={ styles.queryLogList }>
                          { queryLogs.map( ( q, i ) => (
                            <div
                              key={ i }
                              data-testid={ `query-log-entry-${ i }` }
                              className={ styles.queryLogEntry }
                              onClick={ () => { setSqlValue( q.sql ); setActiveResultTab( 'results' ); } }
                              onKeyDown={ ( e ) => { if ( e.key === 'Enter' || e.key === ' ' ) { e.preventDefault(); setSqlValue( q.sql ); setActiveResultTab( 'results' ); } } }
                              role="button"
                              tabIndex={ 0 }
                            >
                              <div className={ styles.queryLogMeta }>
                                <span className={ styles.queryLogTool }>{ q.tool }</span>
                                <span className={ styles.queryLogTime }>
                                  { new Date( q.timestamp ).toLocaleTimeString() }
                                </span>
                              </div>
                              <pre className={ styles.queryLogSql }>{ q.sql }</pre>
                            </div>
                          ) ) }
                        </div>
                      ) }
                    </div>
                  ) }
                </div>
              </Panel>
            </Group>
          </Panel>

          <Separator className={ styles.resizeHandle } style={ { flexBasis: '4px' } } />

          {/* Right Panel: Agent Chat */}
          <Panel defaultSize="28%" minSize="20%" maxSize="40%">
            <ExplorerChat
              id={ id }
              input={ input }
              isWaiting={ isWaiting }
              messages={ displayMessages }
              messagesEndRef={ messagesEndRef }
              onClickChat={ onClickChat }
              onClickStop={ handleStop }
              onInputChange={ onInputChange }
            />
          </Panel>
        </Group>
      </div>
    </div>
  );
}
